'use client'

import { useState, useEffect, useTransition } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Search, Filter, SortAsc } from "lucide-react"

interface SupportCategoryFiltersProps {
  categoryName: string
  categorySlug: string
}

export function SupportCategoryFilters({ categoryName, categorySlug }: SupportCategoryFiltersProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()

  // State for form values
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '')
  const [typeValue, setTypeValue] = useState(searchParams.get('type') || 'all')
  const [sortValue, setSortValue] = useState(searchParams.get('sort') || 'recent')

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      updateURL({ search: searchValue })
    }, 500)

    return () => clearTimeout(timer)
  }, [searchValue])

  const updateURL = (updates: Record<string, string>) => {
    const params = new URLSearchParams(searchParams.toString())
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'all' || (key === 'sort' && value === 'recent')) {
        params.delete(key)
      } else {
        params.set(key, value)
      }
    })

    const queryString = params.toString()
    const newUrl = `/support/${categorySlug}${queryString ? `?${queryString}` : ''}`
    
    startTransition(() => {
      router.push(newUrl)
    })
  }

  const handleTypeChange = (value: string) => {
    setTypeValue(value)
    updateURL({ type: value })
  }

  const handleSortChange = (value: string) => {
    setSortValue(value)
    updateURL({ sort: value })
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 mb-8">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input 
          placeholder={`Search ${categoryName.toLowerCase()}...`}
          value={searchValue}
          onChange={handleSearchChange}
          className="pl-10"
          disabled={isPending}
        />
      </div>
      <div className="flex gap-2">
        <Select value={typeValue} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-40" disabled={isPending}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="guide">Guides</SelectItem>
            <SelectItem value="faq">FAQs</SelectItem>
            <SelectItem value="link">Links</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortValue} onValueChange={handleSortChange}>
          <SelectTrigger className="w-40" disabled={isPending}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most Recent</SelectItem>
            <SelectItem value="title">Alphabetical</SelectItem>
            <SelectItem value="views">Most Viewed</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}